import { useCallback, useEffect, useRef, useState } from 'react'
import { ErrorApi } from '../api/cliente'
import { EscanerCamara, hayCamaraPosible } from '../componentes/EscanerCamara'
import { dinero } from '../api/formato'
import { catalogo, ventas as apiVentas } from '../api/servicios'
import type { Categoria, TipoProducto, UnidadMedida } from '../api/tipos'
import './carga.css'

/**
 * Carga del catálogo con la pistola.
 *
 * Existe por una sola razón: dar de alta ochocientos a mil quinientos productos
 * es el costo escondido de toda puesta en marcha, y hacerlo abriendo un diálogo
 * por producto son varias jornadas. Acá el foco no sale nunca del campo de
 * código y no hace falta tocar el mouse: se escanea, se escribe nombre y
 * precio, Enter, y viene el siguiente.
 *
 * Las decisiones que la hacen rápida, en orden de cuánto ahorran:
 *
 * 1. La categoría, la unidad y el tipo **se recuerdan** entre productos. Quien
 *    carga lo hace por góndola, y en una góndola todo es lo mismo. Sin esto
 *    habría que elegir tres cosas por producto.
 * 2. El SKU se deriva del código de barras. Inventar un SKU por producto
 *    mataría el ritmo, y el resolvedor busca por SKU cuando el código no está
 *    en la tabla, así que sirve igual.
 * 3. Escanear algo ya cargado no es un error: lo dice y sigue. Durante una
 *    carga larga se reescanea sin querer todo el tiempo.
 */
export function CargaProductos() {
    const [categorias, setCategorias] = useState<Categoria[]>([])
    const [unidades, setUnidades] = useState<UnidadMedida[]>([])

    // Lo que se recuerda entre un producto y el siguiente.
    const [categoriaId, setCategoriaId] = useState<number | null>(null)
    const [unidadId, setUnidadId] = useState<number | null>(null)
    const [tipo, setTipo] = useState<TipoProducto>('Unitario')

    const [codigo, setCodigo] = useState('')
    const [enAlta, setEnAlta] = useState<{ codigo: string } | null>(null)

    const [sku, setSku] = useState('')
    const [nombre, setNombre] = useState('')
    const [precio, setPrecio] = useState('')

    const [cargados, setCargados] = useState<{ nombre: string; codigo: string; precio: number }[]>([])

    const [error, setError] = useState<string | null>(null)
    const [nota, setNota] = useState<string | null>(null)
    const [ocupado, setOcupado] = useState(false)
    const [camara, setCamara] = useState(false)

    const campoCodigo = useRef<HTMLInputElement>(null)
    const campoNombre = useRef<HTMLInputElement>(null)

    const volverAEscanear = useCallback(() => {
        setEnAlta(null)
        setSku('')
        setNombre('')
        setPrecio('')
        setCodigo('')
        // Un tiempo para que el campo exista de nuevo antes de enfocarlo.
        setTimeout(() => campoCodigo.current?.focus(), 0)
    }, [])

    useEffect(() => {
        void (async () => {
            try {
                const [c, u] = await Promise.all([catalogo.categorias(), catalogo.unidades()])
                setCategorias(c)
                setUnidades(u)
                setCategoriaId(c[0]?.categoriaId ?? null)
                setUnidadId(u.find((x) => x.codigo === 'UND')?.unidadMedidaId ?? u[0]?.unidadMedidaId ?? null)
            } catch {
                setError('No se pudieron cargar las categorías y unidades.')
            }
        })()

        campoCodigo.current?.focus()
    }, [])

    async function alEscanear(desde?: string) {
        // Con la pistola el codigo ya esta en el campo; con la camara llega por
        // parametro. De aqui para abajo es exactamente el mismo camino.
        const buscado = (desde ?? codigo).trim()
        if (buscado === '' || ocupado) return

        setOcupado(true)
        setError(null)
        setNota(null)

        try {
            const existente = await apiVentas.resolver(buscado)

            // Ya estaba. No es un error: durante una carga larga se reescanea
            // sin querer a cada rato.
            setNota(`Ya estaba cargado: ${existente.nombre} — ${dinero(existente.precioUnitario)}`)
            setCodigo('')
        } catch (fallo) {
            if (fallo instanceof ErrorApi && fallo.estado === 404) {
                abrirAlta(buscado)
                return
            }

            // Un código de peso embebido o dado de baja llega acá con su
            // mensaje propio, que explica qué hacer mejor que uno genérico.
            setError(fallo instanceof ErrorApi ? fallo.mensajeVisible() : 'No se pudo consultar el código.')
        } finally {
            setOcupado(false)
        }
    }

    function abrirAlta(codigoBarra: string) {
        setEnAlta({ codigo: codigoBarra })
        // El SKU sale del código para no tener que inventar uno por producto.
        // Se deja visible y editable: hay locales con su propia numeración.
        setSku(codigoBarra.replace(/[^A-Za-z0-9._-]/g, '').slice(0, 40))
        setNombre('')
        setPrecio('')
        setTimeout(() => campoNombre.current?.focus(), 0)
    }

    async function crear() {
        if (!enAlta || ocupado) return

        const valor = Number(precio)

        if (nombre.trim() === '') {
            setError('Falta el nombre.')
            campoNombre.current?.focus()
            return
        }

        if (!Number.isFinite(valor) || valor < 1) {
            setError('El precio tiene que ser mayor que cero.')
            return
        }

        if (categoriaId === null || unidadId === null) {
            setError('Elija una categoría y una unidad.')
            return
        }

        setOcupado(true)
        setError(null)

        try {
            await catalogo.crearProducto({
                sku: sku.trim() || enAlta.codigo,
                nombre: nombre.trim(),
                categoriaId,
                unidadMedidaId: unidadId,
                tipo,
                stockMinimo: 0,
                precio: valor,
                codigoBarra: enAlta.codigo || undefined,
            })

            setCargados((previos) => [
                { nombre: nombre.trim(), codigo: enAlta.codigo, precio: valor },
                ...previos,
            ])

            setNota(null)
            volverAEscanear()
        } catch (fallo) {
            setError(fallo instanceof ErrorApi ? fallo.mensajeVisible() : 'No se pudo crear el producto.')
        } finally {
            setOcupado(false)
        }
    }

    const unidad = unidades.find((u) => u.unidadMedidaId === unidadId)

    return (
        <div className="columna g4 carga">
            <header className="columna g1">
                <h1>Carga de productos</h1>
                <p className="tenue">
                    Escanee el producto. Si no está en el sistema, se abre el alta con el código ya
                    puesto. La categoría, la unidad y el tipo se mantienen para el siguiente.
                </p>
            </header>

            {error && <p className="aviso aviso-error">{error}</p>}
            {nota && <p className="aviso aviso-ok">{nota}</p>}

            {/* Lo que se repite queda arriba y no se vuelve a tocar. */}
            <section className="panel columna g3">
                <h2 className="carga-titulo">Para todos los que siguen</h2>

                <div className="fila envuelve g3">
                    <div className="carga-campo">
                        <label className="etiqueta" htmlFor="carga-categoria">
                            Categoría
                        </label>
                        <select
                            id="carga-categoria"
                            className="campo"
                            value={categoriaId ?? ''}
                            onChange={(e) => setCategoriaId(Number(e.target.value))}
                        >
                            {categorias.map((c) => (
                                <option key={c.categoriaId} value={c.categoriaId}>
                                    {c.nombre}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="carga-campo">
                        <label className="etiqueta" htmlFor="carga-unidad">
                            Unidad
                        </label>
                        <select
                            id="carga-unidad"
                            className="campo"
                            value={unidadId ?? ''}
                            onChange={(e) => setUnidadId(Number(e.target.value))}
                        >
                            {unidades.map((u) => (
                                <option key={u.unidadMedidaId} value={u.unidadMedidaId}>
                                    {u.nombre}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="carga-campo">
                        <label className="etiqueta" htmlFor="carga-tipo">
                            Tipo
                        </label>
                        <select
                            id="carga-tipo"
                            className="campo"
                            value={tipo}
                            onChange={(e) => setTipo(e.target.value as TipoProducto)}
                        >
                            <option value="Unitario">Unitario</option>
                            <option value="Pesable">Pesable</option>
                        </select>
                    </div>
                </div>

                {tipo === 'Pesable' && unidad?.codigo === 'UND' && (
                    <p className="aviso">
                        Un producto pesable con unidad «{unidad.nombre}» se va a vender de a una
                        pieza. Si se vende por peso, elija kilogramo o litro.
                    </p>
                )}
            </section>

            {enAlta === null ? (
                <section className="panel columna g3">
                    <label className="etiqueta" htmlFor="carga-codigo">
                        Escanee el código de barras
                    </label>
                    <input
                        id="carga-codigo"
                        ref={campoCodigo}
                        className="campo carga-lector"
                        autoComplete="off"
                        maxLength={80}
                        value={codigo}
                        disabled={ocupado}
                        onChange={(e) => setCodigo(e.target.value)}
                        onKeyDown={(evento) => {
                            if (evento.key === 'Enter') {
                                evento.preventDefault()
                                void alEscanear()
                            }
                        }}
                        // El foco vuelve al campo porque la pistola escribe
                        // donde este el cursor. Menos con la camara abierta: ahi
                        // el dialogo lo necesita.
                        onBlur={() => setTimeout(() => {
                            if (!camara) campoCodigo.current?.focus()
                        }, 0)}
                    />

                    <div className="fila envuelve g2">
                        {hayCamaraPosible() && (
                            <button
                                type="button"
                                className="boton"
                                disabled={ocupado}
                                onClick={() => setCamara(true)}
                            >
                                Usar la cámara
                            </button>
                        )}

                        <button
                            type="button"
                            className="boton"
                            onClick={() => abrirAlta('')}
                            disabled={ocupado}
                        >
                            Producto sin código
                        </button>
                        <span className="tenue">
                            Para lo que no trae código de fábrica: pan, huevos sueltos, granel.
                        </span>
                    </div>
                </section>
            ) : (
                <section className="panel columna g3">
                    <div className="fila entre g2">
                        <h2 className="carga-titulo">Producto nuevo</h2>
                        <code className="tenue">{enAlta.codigo || 'sin código'}</code>
                    </div>

                    <div className="fila envuelve g3">
                        <div className="carga-campo carga-campo-ancho">
                            <label className="etiqueta" htmlFor="carga-nombre">
                                Nombre
                            </label>
                            <input
                                id="carga-nombre"
                                ref={campoNombre}
                                className="campo"
                                autoComplete="off"
                                maxLength={160}
                                value={nombre}
                                onChange={(e) => setNombre(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && void crear()}
                            />
                        </div>

                        <div className="carga-campo">
                            <label className="etiqueta" htmlFor="carga-precio">
                                Precio
                            </label>
                            <input
                                id="carga-precio"
                                className="campo campo-numero"
                                type="number"
                                min={1}
                                step={1}
                                inputMode="numeric"
                                value={precio}
                                onChange={(e) => setPrecio(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && void crear()}
                            />
                        </div>

                        <div className="carga-campo">
                            <label className="etiqueta" htmlFor="carga-sku">
                                SKU
                            </label>
                            <input
                                id="carga-sku"
                                className="campo"
                                autoComplete="off"
                                maxLength={40}
                                value={sku}
                                onChange={(e) => setSku(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && void crear()}
                            />
                        </div>
                    </div>

                    <div className="fila envuelve g2">
                        <button
                            type="button"
                            className="boton boton-primario"
                            onClick={() => void crear()}
                            disabled={ocupado}
                        >
                            Guardar y seguir
                        </button>
                        <button type="button" className="boton" onClick={volverAEscanear} disabled={ocupado}>
                            Cancelar
                        </button>
                        <span className="tenue">Enter guarda desde cualquier campo.</span>
                    </div>
                </section>
            )}

            {cargados.length > 0 && (
                <section className="panel columna g2">
                    <h2 className="carga-titulo">
                        Cargados en esta sesión: {cargados.length}
                    </h2>

                    {/* Sirve para ver el avance y para notar un error recién
                        cometido, que es cuando todavía es barato arreglarlo. */}
                    <ul className="carga-lista">
                        {cargados.slice(0, 12).map((p) => (
                            <li key={`${p.codigo}-${p.nombre}`} className="fila entre g2">
                                <span>{p.nombre}</span>
                                <span className="tenue">
                                    {p.codigo || 'sin código'} · {dinero(p.precio)}
                                </span>
                            </li>
                        ))}
                    </ul>

                    {cargados.length > 12 && (
                        <p className="tenue">y {cargados.length - 12} más</p>
                    )}
                </section>
            )}

            {camara && (
                <EscanerCamara
                    // 'unico': se lee un codigo y se cierra, porque lo que sigue
                    // es completar el formulario de alta de ese producto.
                    modo="unico"
                    titulo="Escanear el código del producto"
                    alLeer={(leido) => {
                        setCodigo(leido)
                        void alEscanear(leido)
                    }}
                    alCerrar={() => {
                        setCamara(false)
                        campoCodigo.current?.focus()
                    }}
                />
            )}
        </div>
    )
}
